import {Storage} from "@/Services/Storage";
import axios from "axios";

export default {
    name: 'tax',
    state: {
        taxLabels: [],
        zraSupportedCurrencies : [],
        hasDocsSubmitted: false,
        isSmartInvoiceLive: true,
    },
    getters: {
        getTaxLabels: (state) => {
           return  state.taxLabels
        },
        getDocSubmissionStatus: (state) => state.hasDocsSubmitted,
        getZraSupportedCurrencies: (state) => state.zraSupportedCurrencies,
        getSmartInvoicingLive: (state) => state.isSmartInvoiceLive,
        getZraCategoryByLabel: (state) => (taxLabel) => {
            return state.taxLabels.find(category => category.TaxLabel?.toLowerCase() === taxLabel?.toLowerCase());
        }
    },
    mutations: {
        setTaxLabels: async (state, payload) => {
            if (payload) {
                state.taxLabels = payload;
                await Storage.setTaxLabels(payload);
            }
            else payload = await Storage.getAllTaxLabels();
            state.taxLabels = payload;
        },
        setDocSubmissionStatus: (state, data) => state.hasDocsSubmitted = data,
        setZraSupportedCurrencies: (state, data) => state.zraSupportedCurrencies = data,
    },
    actions: {
        getAllTaxLabels: async (context) => {
            let storeDB = await Storage.getCurrentStoreDB();
            return axios.get(backOfficeTomcatApi + `zra_category/${storeDB}/getAll`)
                .then(({data}) => context.commit(`setTaxLabels`, data))
                .catch(err => errorHandler.tomcatError(err))
        },
/**
         * Verify taxpayer details by TPIN using the Franchise API.
         * Notes:
         * - If no TPIN is provided, this action exits gracefully to avoid calling /TPIN/undefined.
         * - Intended for places where a user enters a TPIN (e.g., Debtors/Suppliers).
         * - For store VAT checks without a TPIN argument, use the settings-module action of the same name.
         * @param {Object} context - Vuex context
         * @param {string} tpin - Taxpayer Identification Number
         * @returns {Promise<Object|null>} Taxpayer data when valid; null when TPIN missing/invalid
         */
        checkZRADetails: async (context, tpin) => {
            try {
                // Guard: avoid network call when TPIN is missing/invalid
                if (!tpin || !isValidString(tpin)) {
                    return null;
                }
                tpin = tpin.toString().trim();
                return axios.get(franchiseTomcatApi + `AndroidLogin/TPIN/${tpin}`)
                    .then(({data}) => {
                        if (data?.tpin) {
                            return data;
                        } else if (data && data.toString && data.toString().includes(`Send TPIN:`)) {
                            throw new Error(`Invalid TPIN`);
                        }
                        return null;
                    })
                    .catch(err => errorHandler.tomcatError(err));
            } catch (e) {
                return errorHandler.tomcatError(e);
            }
        },
/**
         * Verify taxpayer details by TPIN (variant 2).
         * Behavior mirrors checkZRADetails but may be used in other flows (e.g., supplier/GRV).
         * @param {Object} context - Vuex context
         * @param {string} tpin - Taxpayer Identification Number
         * @returns {Promise<Object|null>} Taxpayer data when valid; null when TPIN missing/invalid
         */
        checkZRADetails2: async (context, tpin) => {
            try {
                if (!tpin || !isValidString(tpin)) {
                    return null;
                }
                tpin = tpin.toString().trim();
                return axios.get(franchiseTomcatApi + `AndroidLogin/TPIN/${tpin}`)
                    .then(({data}) => {
                        if (data?.tpin) {
                            return data;
                        } else if (data && data.toString && data.toString().includes(`Send TPIN:`)) {
                            throw new Error(`Invalid TPIN`);
                        }
                        return null;
                    });
            } catch (e) {
                return errorHandler.tomcatError(e);
            }
        },
        retrySmartInvoice: async (context, invoiceNumber) => {
            let store = await Storage.getCurrentStoreDB()
            return axios.get(backOfficeTomcatApi+`other/${store}/sendSmartInvoice?invoice=`+invoiceNumber)
                .then(({data}) => {
                    return data
                })
                .catch(err => {
                    // Handle NullPointerException for missing refund number
                    if (err.response &&
                        err.response.status === 500 &&
                        err.response.data &&
                        err.response.data.response &&
                        err.response.data.response.type &&
                        err.response.data.response.type.includes("NullPointerException") &&
                        err.response.data.response.type.includes("getRefundNumber()")) {
                        return Swal.fire({
                            title: 'Smart Invoice Error',
                            icon: 'warning',
                            html: `<p>Unable to process smart invoice due to missing refund number.</p>
                           <p>Please contact support: +260958168829</p>`,
                            showCloseButton: true,
                        });
                    }

                    const sanitizeErrorMessage = (message) => {
                        if (!message) return 'An error occurred';
                        return message.replace(/[<>]/g, match => match === '<' ? '&lt;' : '&gt;');
                    };

                    if (err.response && err.response.data && err.response.data.response) {
                        const rawErrorMessage = err.response.data.response.Message || err.response.data?.response?.message || 'An error occurred';
                        const errorMessage = sanitizeErrorMessage(rawErrorMessage);
                        const errorCode = err.response.data.response.Code || err.response.data.code || '';

                        return Swal.fire({
                            title: 'Smart Invoice Error',
                            icon: 'warning',
                            html: `${errorCode ? `<p>ZRA failure Code: ${errorCode}</p>` : ''}
                           <p>${errorMessage}</p>
                           <p>Please contact support: +260958168829 </p>`,
                            showCloseButton: true,
                        });
                    }
                    return errorHandler.tomcatError(err);
                });
        },
        searchTaxPayers: async (context, name) => {
            return axios.get(franchiseTomcatApi + `zrataxinfo/searchTaxPayer?name=${name}`)
                .then(({data}) => data)
                .catch(err => errorHandler.tomcatError(err))
        },



        saveSmartInvoiceData: async (context, payload) => {
            let storeDB = await Storage.getCurrentStoreDB();
            return axios.post(franchiseTomcatApi + `zra_monitoringinfo/${storeDB}`, payload)
                .then(({data}) => context.commit(`setSmartInvoicingStatus`, data))
                .catch(err => errorHandler.tomcatError(err))
        },

        getFranchiseStoresByGroup: async () => {
            let storeDetails = await Storage.getCurrentStore();
            let group = storeDetails.Group;
            return axios.get(backOfficeTomcatApi + `stores/getByGroup?group=` + group)
                .then(({data}) => data)
                .catch(err => errorHandler.tomcatError(err))
        },
        makePaymentForSmartInvoiceSignUp: async (context, payload) => {
            const apiEndpoint = ticketApi + 'smartInvoiceOnboard/makeSmartInvoicePayment';
            let DB = await Storage.getCurrentStoreDB();
            // DB = window.btoa(DB);
            let user = await Storage.getCurrentUser();
            const obj = {
                ...payload,
                paidBy: user.Username
            };
            return axios.post(apiEndpoint, obj, {
                headers: {DB}
            }).catch(err => errorHandler.tomcatError(err))
        },
        submitSmartInvoiceSignUpDocs: async (context, payload) => {
            let user = await Storage.getCurrentUser();
            let username = user.Name + " " + user.Surname;
            const apiEndpoint = ticketApi + 'smartInvoiceOnboard/submitDocs';
            const data = {
                ...payload,
                submittedBy: username,
                platform: 'Online Back-Office',
            };
            let DB = await Storage.getCurrentStoreDB();
            DB = window.btoa(DB);
            return axios.post(apiEndpoint, data, {
                headers: {DB}
            }).then(({data})=>data).catch(err => errorHandler.tomcatError(err))
        },
        checkIfStoreDocsSubmitted: async (context) => {
            let DB = await Storage.getCurrentStoreDB();
            DB = window.btoa(DB);
            return axios.get(ticketApi + `smartInvoiceOnboard/checkDocumentSubmissionStatus`, {
                headers: {DB}
            }).then(({data}) => context.commit(`setDocSubmissionStatus`, data))

        },

        getCommitmentForm: async (context, payload) => {
            let DB = payload.DB
            return axios.post(synergyApi + `store-registration/export-pdf/smart-invoice`, payload, {
                headers: { DB },
                responseType: `blob`,
            }).then(async ({data}) => {
                let base64 = await getBase64(data);
                base64 = stripMimeType(base64);
                // const blob = new Blob([base64], { type: `application/pdf` });
                // let url = URL.createObjectURL(blob);
                // window.open(url);
                return base64;
            }).catch(err => {
                errorHandler.tomcatError(err)
            })
        },
        getBranchForm: async (context, payload) => {
            let DB = payload.DB
            return axios.post(synergyApi + `store-registration/export-pdf/branch-details`, payload,{
                headers: { DB },
                responseType: `blob`,
            }).then(async ({data}) => {
                let base64 = await getBase64(data);
                base64 = stripMimeType(base64);
                return base64;
            })
        },
        getZraSupportedCurrencies: async (context) => {
            return axios.post(backOfficeTomcatApi + `currency/too_cool_main/getZraSupportedCountries`)
                .then(({data}) => context.commit(`setZraSupportedCountries`,data))
        },
    }
}
